import * as React from 'react';
import { MeatballMenu, PromptConfirmationModal } from 'ui';
import { ComplexValue, ComponentProps } from 'ui';
import { filesize } from 'filesize';
import { FileItemProgress } from 'ui/file-uploader/file-uploader-item-progress';
import { RemoteImage } from 'ui/remote-blob';
import { FileUploaderItemEditTitle } from 'ui/file-uploader/file-uploader-item-edit-title';

import { MdEdit, MdStar } from 'react-icons/md';

import styles from './file-uploader.scss';
import { ObjectFitImage } from 'ui/object-fit-image';
import { MalwareScanStatus, MalwareScanStatusValue } from 'ui/file-uploader/malware-scan-status';
import { BlobMalwareScanStatus } from 'ui/file-uploader/file';

export interface FileUploaderItemProps extends ComponentProps {
  id?: number;
  blobId?: number;
  title: string;
  name?: string;
  extension?: string;
  type: string;
  attachedBy: ComplexValue<number>;
  attachedAt: Date;
  size?: number;
  thumbnailSize?: number;
  isWaiting: boolean;
  uploaded?: number;
  uploadRate?: number;
  mainImage?: boolean;
  malwareScanStatus?: MalwareScanStatusValue;
  print?: boolean;
  createDownloader?: (blobId: number, name: string) => Promise<void>;
  loadBlob: (blobId: number, thumbnail: boolean) => Promise<Blob>;
  textHourPostfix?: string;
  textMinutePostfix?: string;
  textSecondPostfix?: string;
  textNotAvailable?: string;
  textDone?: string;
  textEditTitle?: string;
  textDownloadFile?: string;
  textDelete?: string;
  textDeleteMessage?: string;
  textUseAsMainImage?: string;
  textContinue?: string;
  textCancel?: string;
  deleteItem?: (id: number) => void;
  updateItemTitle?: (id: number, name: string) => void;
  onClick: ( id:number ) => void;
  setItemAsMain?: (id: number) => void;
}

export interface FileUploaderItemState {
  downloading: boolean;
  editingTitle: boolean;
}

export class FileUploaderItem extends React.Component<FileUploaderItemProps, FileUploaderItemState> {
  public state: Readonly<FileUploaderItemState> = {
    downloading: false,
    editingTitle: false
  };
  private meatballContainer:React.RefObject<HTMLDivElement> = React.createRef();

  private onClickThrough = () => {
    this.props.onClick(this.props.id);
  };

  private onDownload = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    this.meatballContainer && this.meatballContainer.current && this.meatballContainer.current.focus();

    if (
      !this.state.downloading &&
      !!this.props.attachedAt &&
      this.props.blobId
    ) {
      this.setState({ downloading: true } as FileUploaderItemState);
      await this.props.createDownloader(this.props.blobId, this.props.name);
      this.setState({ downloading: false } as FileUploaderItemState);
    }
  };

  private onSetMainImage = () => {
    this.props.setItemAsMain && this.props.setItemAsMain(this.props.id);
    this.meatballContainer && this.meatballContainer.current && this.meatballContainer.current.focus();
  };

  private displayExtension() {
    if (this.props.thumbnailSize && this.props.thumbnailSize > 0) {
      return null;
    }

    return <div className={styles.attachmentExt}>{this.props.extension}</div>;
  }

  private renderMetadata() {
    if (!this.props.attachedAt) {
      return null;
    }

    const attachedAt = this.props.attachedAt
      ? this.props.formatDate(this.props.attachedAt) +
        ', ' +
        this.props.formatTime(this.props.attachedAt)
      : this.props.textNotAvailable;
    const attachedBy = this.props.attachedBy
      ? this.props.attachedBy.description
      : '';
    const size = filesize(this.props.size || 0).toString();

    return (
      <ul className={styles.metadata}>
        {attachedBy && <li>{attachedBy}</li>}
        {attachedAt && <li>{attachedAt}</li>}
        {size && <li>{size}</li>}
        <li><MalwareScanStatus {...this.props.malwareScanStatus} /></li>
      </ul>
    );
  }

  private humanize(duration: number) {
    const secs = Math.floor(duration / 1000);
    const mins = Math.floor(secs / 60);
    const hrs = Math.floor(mins / 60);

    if (hrs === Infinity) {
      return this.props.textNotAvailable;
    }

    if (hrs > 0) {
      return hrs + this.props.textHourPostfix;
    }
    if (mins > 0) {
      return (mins % 60) + this.props.textMinutePostfix;
    }
    const rest = secs % 60;
    return (rest >= 0 ? rest : 0) + this.props.textSecondPostfix;
  }

  private renderProgressInfo() {
    if (!!this.props.attachedAt) {
      return null;
    }

    const size = filesize(this.props.size || 0);
    const uploaded = filesize(this.props.uploaded || 0);
    const uploadRate =
      this.props.uploadRate !== Infinity && this.props.uploadRate > 0
        ? filesize(this.props.uploadRate || 0) + '/s'
        : this.props.textNotAvailable;
    const estimate = this.humanize(
      ((this.props.size - this.props.uploaded) / this.props.uploadRate) * 1000
    );

    return (
      <ul className={styles.metadata}>
        <li>{uploaded + ' / ' + size}</li>
        <li>{uploadRate}</li>
        <li>{estimate}</li>
      </ul>
    );
  }

  private onEditTitle = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ editingTitle: true } as FileUploaderItemState);
  };

  private onChangeTitle = (newTitle: string) => {
    if (!newTitle.trim()) {
      // We never want blank file titles.
      // In case of blank title, we reset to filename with extension.
      newTitle = this.props.name.substring(0, this.props.name.lastIndexOf('.'));
    }
    if (newTitle !== this.props.title) {
      this.props.updateItemTitle(this.props.id, newTitle);
    }
    this.setState({ editingTitle: false } as FileUploaderItemState);
  };

  private handleDelete = () => {
    this.props.deleteItem(this.props.id);
  };

  private loadBlob = () => {
    return this.props.loadBlob(this.props.blobId, !this.props.print);
  };

  private renderMeatballMenu() {
    if (this.state.editingTitle || !this.props.attachedAt) {
      return false;
    }

    const downloadItem = (<li>
      <button
        type="button"
        className="dropdown-item"
        onClick={this.onDownload}
      >
        {this.props.textDownloadFile}
      </button>
    </li>);

    const setAsMainImage = this.props.setItemAsMain && this.props.thumbnailSize && !this.props.mainImage && (<li>
      <button
        type="button"
        className="dropdown-item"
        onClick={this.onSetMainImage}
      >
        {this.props.textUseAsMainImage}
      </button>
    </li>);

    const deleteItem = !!this.props.deleteItem ? (
      <PromptConfirmationModal
        title={this.props.textDelete}
        description={this.props.textDeleteMessage}
        continueText={this.props.textContinue}
        cancelText={this.props.textCancel}
        onContinue={this.handleDelete}
      >
        <li>
          <button type="button" className="dropdown-item">
            {this.props.textDelete}
          </button>
        </li>
      </PromptConfirmationModal>
    ) : null;

    return (
      <MeatballMenu closeAfterClick={false}>
        {downloadItem}
        {setAsMainImage}
        {deleteItem}
      </MeatballMenu>
    );
  }

  private renderTitle() {
    if (!this.state.editingTitle) {
      return <h5>{this.props.title}</h5>;
    }
    return (
      <FileUploaderItemEditTitle
        title={this.props.title}
        textDone={this.props.textDone}
        onChange={this.onChangeTitle}
      />
    );
  }

  public render() {
    const progress = (this.props.uploaded / this.props.size) * 100;
    const progressBar = (
      <FileItemProgress
        progress={progress}
        indeterminate={this.state.downloading || progress >= 100}
      />
    );

    const thumbnail = this.props.thumbnailSize &&
      this.props.thumbnailSize > 0 && (
        <RemoteImage loadBlob={this.loadBlob}>
          {(objectUrl) => <ObjectFitImage src={objectUrl} cover={true}/>}
        </RemoteImage>
      );

    const clickAction = this.props.type && this.props.type.indexOf('image') !== -1 ? this.onClickThrough : this.onDownload;

    const editTitle = !!this.props.updateItemTitle ? (
      <div className={styles.buttonContainer} title={this.props.textEditTitle} onClick={this.onEditTitle}>
        {!!this.props.updateItemTitle && <MdEdit className={styles.buttonIcons}/>}
      </div>
    ) : null;

    const malwareScanStatus = this.props.malwareScanStatus?.value;
    const malwareScanStyle =
      malwareScanStatus === BlobMalwareScanStatus.Malicious ?
        styles.error : '';

    return (
      <div className={styles.itemWrapper}>
        <div
          tabIndex={0}
          className={`${
            this.state.editingTitle ? styles.isEditing : styles.notEditing
          } ${this.props.mainImage ? styles.main : ''} ${styles.attachment} ${malwareScanStyle}`}
          onClick={this.state.editingTitle ? undefined : clickAction}
        >
          <div className={styles.attachmentImg}>
            {thumbnail}
            {this.displayExtension()}
            { this.props.mainImage && <div className={styles.marked}><MdStar /></div> }
          </div>
          <div className={styles.attachmentBody}>
            {this.renderTitle()}
            {this.renderMetadata()}
            {this.renderProgressInfo()}

          </div>
        </div>
        <div className={styles.dropdown} tabIndex={-1} ref={this.meatballContainer}>{this.renderMeatballMenu()}</div>
        {!this.state.editingTitle && (!this.state.downloading && this.props.attachedAt) && 
          <div className={styles.btnIconsWrapper}>{editTitle}</div>}

        <div className={styles.progressContainer}>
          {!this.props.attachedAt || this.state.downloading
            ? progressBar
            : null}
        </div>
      </div>
    );
  }
}
